import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../lib/hooks/usePageTitle.js';
import { api } from '../../lib/api.js';
import { formatCurrency } from '../../lib/formatters.js';
import { PriorityBadge } from './components/PriorityBadge.js';

interface QueueItem {
  id: string;
  claimNumber: string;
  claimType: string;
  status: string;
  claimedAmount: string;
  isoCurrencyCode: string;
  verificationConfidence: string | null;
  submittedAt: string | null;
  slaTargetSettleAt?: string | null;
  distributorName: string;
  aiVerdict: string | null;
  priorityScore: number;
  incompleteDocsOverride?: boolean;
}

const REJECT_REASONS = [
  'INSUFFICIENT_DOCUMENTATION',
  'AMOUNT_MISMATCH',
  'SCHEME_INELIGIBLE',
  'DUPLICATE_CLAIM',
  'OUTSIDE_VALID_PERIOD',
  'OTHER',
] as const;

const VERDICT_COLORS: Record<string, string> = {
  AUTO_APPROVABLE: 'bg-success/10 text-success',
  NEEDS_REVIEW: 'bg-copper-soft text-copper-deep',
  INSUFFICIENT_EVIDENCE: 'bg-slate-100 text-slate-500',
  LIKELY_REJECT: 'bg-danger/10 text-danger',
};

function daysAgo(dateStr: string | null): number {
  if (!dateStr) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24)));
}

function isSlaRisk(slaTargetSettleAt: string | null | undefined): boolean {
  if (!slaTargetSettleAt) return false;
  const daysUntil = (new Date(slaTargetSettleAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return daysUntil <= 5 && daysUntil >= 0;
}

function confidenceBadgeClass(conf: string | null): string {
  if (conf == null) return 'bg-slate-100 text-slate-400';
  const n = Number(conf);
  if (n >= 0.85) return 'bg-success/10 text-success';
  if (n >= 0.6) return 'bg-copper-soft text-copper-deep';
  return 'bg-danger/10 text-danger';
}

function Modal({ open, onClose, children }: { open: boolean; onClose: () => void; children: React.ReactNode }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded shadow-xl max-w-md w-full mx-4 p-6 space-y-4">
        {children}
      </div>
    </div>
  );
}

export function ApprovalQueuePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  usePageTitle('approver.queue.title');

  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // ── Filters ──────────────────────────────────────────────────────────────────
  const [filterType, setFilterType] = useState('');
  const [filterVerdict, setFilterVerdict] = useState('');

  // Derived filter options from loaded data
  const claimTypeOptions = useMemo(() => [...new Set(queue.map((q) => q.claimType))].sort(), [queue]);
  const verdictOptions = useMemo(() => [...new Set(queue.map((q) => q.aiVerdict).filter(Boolean))].sort() as string[], [queue]);

  // Only AWAITING_REVIEW items are selectable; UNDER_REVIEW are visible but read-only
  const filteredQueue = useMemo(() => queue.filter((q) =>
    (!filterType || q.claimType === filterType) &&
    (!filterVerdict || q.aiVerdict === filterVerdict),
  ), [queue, filterType, filterVerdict]);

  const selectableInView = filteredQueue.filter((q) => q.status === 'AWAITING_REVIEW');

  // ── Bulk state ────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [rejectComment, setRejectComment] = useState('');

  const fetchQueue = useCallback(() => {
    setLoading(true);
    api.get<QueueItem[]>('/approver/queue')
      .then((data) => { setQueue(data); setLoading(false); })
      .catch(() => { setQueue([]); setLoading(false); });
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // ── Selection helpers ─────────────────────────────────────────────────────────
  const selectedItems = queue.filter((q) => selected.has(q.id));
  const selectedTotal = selectedItems.reduce((sum, q) => sum + Number(q.claimedAmount), 0);
  const selectedCurrency = selectedItems[0]?.isoCurrencyCode ?? 'IDR';

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setSuccessMsg(null);
  }

  // Selects/deselects all AWAITING_REVIEW items in the current filtered view
  function toggleSelectAll() {
    const allSelectableIds = new Set(selectableInView.map((q) => q.id));
    const allSelected = selectableInView.every((q) => selected.has(q.id));
    if (allSelected && selectableInView.length > 0) {
      // Deselect all in view (keep selections outside this filter intact)
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of allSelectableIds) next.delete(id);
        return next;
      });
    } else {
      // Select all in view
      setSelected((prev) => new Set([...prev, ...allSelectableIds]));
    }
    setSuccessMsg(null);
  }

  // "Select all X matching" — select EVERY AWAITING_REVIEW item in the current filter
  function selectAllMatching() {
    setSelected(new Set(selectableInView.map((q) => q.id)));
    setSuccessMsg(null);
  }

  const allInViewSelected = selectableInView.length > 0 && selectableInView.every((q) => selected.has(q.id));
  const someInViewSelected = selectableInView.some((q) => selected.has(q.id));
  const filtersActive = !!(filterType || filterVerdict);

  // ── Optimistic remove ─────────────────────────────────────────────────────────
  function optimisticRemove(ids: Set<string>): () => void {
    const prev = queue;
    setQueue((q) => q.filter((item) => !ids.has(item.id)));
    setSelected(new Set());
    return () => setQueue(prev);
  }

  // ── Bulk actions ──────────────────────────────────────────────────────────────
  async function confirmBulkApprove() {
    setApproveModalOpen(false);
    setSubmitting(true);
    setBulkError(null);
    const ids = Array.from(selected);
    const rollback = optimisticRemove(selected);
    try {
      const result = await api.post<{ approvedCount: number }>('/approver/claims/bulk-approve', { claimIds: ids });
      setSuccessMsg(t('approver.queue.successApproved', { count: result.approvedCount }));
      setTimeout(fetchQueue, 2000);
    } catch (err) {
      rollback();
      setBulkError(err instanceof Error ? err.message : t('errors.internalServerError'));
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmBulkReject() {
    if (!rejectReason) return;
    setRejectModalOpen(false);
    setSubmitting(true);
    setBulkError(null);
    const ids = Array.from(selected);
    const rollback = optimisticRemove(selected);
    try {
      const result = await api.post<{ rejectedCount: number }>('/approver/claims/bulk-reject', {
        claimIds: ids,
        reason: t(`approver.queue.rejectReasons.${rejectReason}`, { defaultValue: rejectReason }),
        ...(rejectComment.trim() ? { comment: rejectComment.trim() } : {}),
      });
      setSuccessMsg(t('approver.queue.successRejected', { count: result.rejectedCount }));
      setRejectReason('');
      setRejectComment('');
      setTimeout(fetchQueue, 2000);
    } catch (err) {
      rollback();
      setBulkError(err instanceof Error ? err.message : t('errors.internalServerError'));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <h1 className="font-serif italic text-3xl text-ink">
          {t('approver.queue.title', { defaultValue: 'Approval Queue' })}
        </h1>
        <span className="text-sm font-semibold px-2.5 py-1 bg-copper-soft text-copper-deep rounded-sm">
          {loading ? '…' : filteredQueue.length}
        </span>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-hairline">
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 uppercase tracking-wide">
            {t('approver.queue.filterType')}
          </label>
          <select
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value); setSelected(new Set()); }}
            className="px-2 py-1.5 border border-hairline rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-copper"
          >
            <option value="">{t('approver.queue.all')}</option>
            {claimTypeOptions.map((ct) => (
              <option key={ct} value={ct}>
                {t(`wizard.claimTypes.${ct}.label`, { defaultValue: ct })}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 uppercase tracking-wide">
            {t('approver.queue.filterVerdict')}
          </label>
          <select
            value={filterVerdict}
            onChange={(e) => { setFilterVerdict(e.target.value); setSelected(new Set()); }}
            className="px-2 py-1.5 border border-hairline rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-copper"
          >
            <option value="">{t('approver.queue.all')}</option>
            {verdictOptions.map((v) => (
              <option key={v} value={v}>
                {t(`wizard.verdicts.${v}`, { defaultValue: v })}
              </option>
            ))}
          </select>
        </div>

        {filtersActive && (
          <button
            type="button"
            onClick={() => { setFilterType(''); setFilterVerdict(''); setSelected(new Set()); }}
            className="text-xs text-slate-400 hover:text-copper transition-colors"
          >
            {t('approver.queue.clearFilters', { defaultValue: 'Clear filters' })}
          </button>
        )}

        <div className="ml-auto text-xs text-slate-400 self-center">
          {filtersActive
            ? t('approver.queue.showingFiltered', {
                filtered: filteredQueue.length,
                total: queue.length,
                defaultValue: `${filteredQueue.length} of ${queue.length}`,
              })
            : null}
        </div>
      </div>

      {/* Bulk action bar — visible whenever ≥ 1 selected */}
      {selected.size >= 1 && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-copper-soft border border-copper/20 rounded">
          <div className="flex-1 min-w-0 space-y-0.5">
            <div>
              <span className="text-sm text-copper-deep font-semibold">
                {t('approver.queue.selectedCount', { count: selected.size })}
              </span>
              {selectedTotal > 0 && (
                <span className="ml-2 text-xs text-copper-deep/70">
                  {t('approver.queue.totalAmount', {
                    amount: formatCurrency(selectedTotal, selectedCurrency),
                    defaultValue: `Total: ${formatCurrency(selectedTotal, selectedCurrency)}`,
                  })}
                </span>
              )}
            </div>
            {/* "Select all matching" — shown when filter is active and not all matching are selected */}
            {filtersActive && !selectableInView.every((q) => selected.has(q.id)) && selectableInView.length > selected.size && (
              <button
                type="button"
                onClick={selectAllMatching}
                className="text-xs text-copper hover:underline"
              >
                {t('approver.queue.selectAllMatching', {
                  count: selectableInView.length,
                  defaultValue: `Select all ${selectableInView.length} matching claims`,
                })}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={submitting}
              onClick={() => { setBulkError(null); setApproveModalOpen(true); }}
              className="px-4 py-1.5 bg-success text-white rounded text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {submitting
                ? t('approver.queue.approving', { defaultValue: 'Processing...' })
                : t('approver.queue.approveSelected')}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => { setBulkError(null); setRejectModalOpen(true); }}
              className="px-4 py-1.5 border border-danger text-danger rounded text-sm font-semibold hover:bg-danger/5 disabled:opacity-50 transition-colors"
            >
              {t('approver.queue.rejectSelected')}
            </button>
            <button
              type="button"
              onClick={() => { setSelected(new Set()); setBulkError(null); setSuccessMsg(null); }}
              className="text-xs text-slate-500 hover:text-copper transition-colors"
            >
              {t('approver.actions.cancel', { defaultValue: 'Cancel' })}
            </button>
          </div>

          {bulkError && <p className="w-full text-xs text-danger">{bulkError}</p>}
        </div>
      )}

      {/* Success toast */}
      {successMsg && (
        <div className="flex items-center justify-between px-4 py-3 bg-success/5 border border-success/30 rounded text-sm text-success">
          <span>{successMsg}</span>
          <button type="button" onClick={() => setSuccessMsg(null)} className="ml-3 text-success/60 hover:text-success">✕</button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-14 bg-slate-100 rounded" />
          ))}
        </div>
      ) : filteredQueue.length === 0 && !successMsg ? (
        <div className="py-16 text-center border border-hairline rounded bg-bone">
          <p className="text-slate-500">
            {filtersActive
              ? t('approver.queue.emptyFiltered', { defaultValue: 'No claims match the current filters.' })
              : t('approver.queue.empty', { defaultValue: 'No claims awaiting review.' })}
          </p>
          {filtersActive && (
            <button
              type="button"
              onClick={() => { setFilterType(''); setFilterVerdict(''); }}
              className="mt-2 text-xs text-copper hover:underline"
            >
              {t('approver.queue.clearFilters', { defaultValue: 'Clear filters' })}
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline text-xs uppercase tracking-wide text-slate-500">
                {/* Header checkbox selects all AWAITING_REVIEW items in filtered view */}
                <th className="pb-2 pr-3 w-8">
                  <input
                    type="checkbox"
                    checked={allInViewSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someInViewSelected && !allInViewSelected;
                    }}
                    onChange={toggleSelectAll}
                    disabled={selectableInView.length === 0}
                    className="accent-copper"
                  />
                </th>
                <th className="text-left pb-2 pr-4">{t('claims.claimNumber')}</th>
                <th className="text-left pb-2 pr-4">{t('approver.queue.distributor', { defaultValue: 'Distributor' })}</th>
                <th className="text-left pb-2 pr-4">{t('claims.type')}</th>
                <th className="text-right pb-2 pr-4">{t('claims.amount')}</th>
                <th className="text-left pb-2 pr-4">{t('approver.queue.confidence', { defaultValue: 'Confidence' })}</th>
                <th className="text-right pb-2 pr-4">{t('approver.queue.age', { defaultValue: 'Age' })}</th>
                <th className="text-left pb-2 pr-4">{t('approver.queue.verdict', { defaultValue: 'AI Verdict' })}</th>
                <th className="text-left pb-2 pr-4">{t('approver.queue.priority', { defaultValue: 'Priority' })}</th>
                <th className="pb-2">{t('claims.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {filteredQueue.map((item) => {
                const age = daysAgo(item.submittedAt);
                const slaRisk = isSlaRisk(item.slaTargetSettleAt);
                const confPct = item.verificationConfidence != null
                  ? `${Math.round(Number(item.verificationConfidence) * 100)}%`
                  : null;
                const isAwaitingReview = item.status === 'AWAITING_REVIEW';
                const isChecked = selected.has(item.id);

                return (
                  <tr
                    key={item.id}
                    className={['transition-colors', isChecked ? 'bg-copper-soft/40' : 'hover:bg-bone'].join(' ')}
                  >
                    <td className="py-3 pr-3">
                      {isAwaitingReview ? (
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSelect(item.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="accent-copper"
                        />
                      ) : (
                        // UNDER_REVIEW — show a locked indicator, not a checkbox
                        <span className="inline-block w-4 h-4 rounded border border-slate-200 bg-slate-50" title={t('approver.queue.underReviewLocked', { defaultValue: 'Under review' })} />
                      )}
                    </td>

                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => navigate(`/approver/claims/${item.id}`)}
                          className="font-mono text-xs text-copper hover:underline"
                        >
                          {item.claimNumber}
                        </button>
                        {slaRisk && (
                          <span className="text-xs font-semibold px-1.5 py-0.5 rounded-sm bg-danger/10 text-danger">
                            SLA Risk
                          </span>
                        )}
                        {item.incompleteDocsOverride && (
                          <span className="bg-copper-soft text-copper-deep border border-copper/30 px-2 py-0.5 rounded-full text-xs font-mono uppercase tracking-wider">
                            {t('approver.overrideBadge')}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="py-3 pr-4 text-xs text-slate-600 max-w-[120px] truncate">
                      {item.distributorName}
                    </td>

                    <td className="py-3 pr-4 text-xs text-slate-500">
                      {t(`wizard.claimTypes.${item.claimType}.label`, { defaultValue: item.claimType })}
                    </td>

                    <td className="py-3 pr-4 text-right font-mono text-xs">
                      {formatCurrency(Number(item.claimedAmount), item.isoCurrencyCode)}
                    </td>

                    <td className="py-3 pr-4">
                      <span className={['text-xs font-semibold px-2 py-0.5 rounded-sm', confidenceBadgeClass(item.verificationConfidence)].join(' ')}>
                        {confPct ?? '—'}
                      </span>
                    </td>

                    <td className="py-3 pr-4 text-right font-mono text-xs text-slate-500">{age}d</td>

                    <td className="py-3 pr-4">
                      {item.aiVerdict ? (
                        <span className={['text-xs font-semibold px-2 py-0.5 rounded-sm', VERDICT_COLORS[item.aiVerdict] ?? 'bg-slate-100 text-slate-500'].join(' ')}>
                          {t(`wizard.verdicts.${item.aiVerdict}`, { defaultValue: item.aiVerdict })}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>

                    <td className="py-3 pr-4">
                      <PriorityBadge score={item.priorityScore} />
                    </td>

                    <td className="py-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/approver/claims/${item.id}`)}
                        className="px-3 py-1.5 border border-hairline rounded text-xs font-semibold text-slate-600 hover:border-copper hover:text-copper transition-colors"
                      >
                        {t('approver.queue.review', { defaultValue: 'Review →' })}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Approve confirm modal */}
      <Modal open={approveModalOpen} onClose={() => setApproveModalOpen(false)}>
        <h2 className="font-serif italic text-xl text-ink">
          {t('approver.queue.bulkApproveModal.title', { count: selected.size })}
        </h2>
        <p className="text-sm text-slate-600">
          {t('approver.queue.bulkApproveModal.body', {
            count: selected.size,
            total: formatCurrency(selectedTotal, selectedCurrency),
          })}
        </p>
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => { void confirmBulkApprove(); }}
            className="flex-1 py-2 bg-success text-white text-sm font-semibold rounded hover:opacity-90 transition-opacity"
          >
            {t('approver.queue.bulkApproveModal.confirm', { defaultValue: 'Approve All' })}
          </button>
          <button
            type="button"
            onClick={() => setApproveModalOpen(false)}
            className="flex-1 py-2 border border-hairline text-sm text-slate-600 rounded hover:bg-bone transition-colors"
          >
            {t('approver.queue.bulkApproveModal.cancel', { defaultValue: 'Cancel' })}
          </button>
        </div>
      </Modal>

      {/* Reject reason modal */}
      <Modal open={rejectModalOpen} onClose={() => setRejectModalOpen(false)}>
        <h2 className="font-serif italic text-xl text-ink">
          {t('approver.queue.bulkRejectModal.title', { count: selected.size })}
        </h2>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            {t('approver.queue.bulkRejectModal.reasonLabel', { defaultValue: 'Rejection Reason' })}
          </label>
          <select
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="w-full border border-hairline rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-copper"
          >
            <option value="">{t('approver.queue.bulkRejectModal.selectReason', { defaultValue: 'Select a reason...' })}</option>
            {REJECT_REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`approver.queue.rejectReasons.${r}`, { defaultValue: r })}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            {t('approver.queue.bulkRejectModal.commentLabel', { defaultValue: 'Additional comment (optional)' })}
          </label>
          <textarea
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            rows={3}
            className="w-full border border-hairline rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-copper resize-none"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            disabled={!rejectReason}
            onClick={() => { void confirmBulkReject(); }}
            className="flex-1 py-2 bg-danger text-white text-sm font-semibold rounded hover:opacity-90 disabled:opacity-40 transition-opacity"
          >
            {t('approver.queue.bulkRejectModal.confirm', { defaultValue: 'Reject All' })}
          </button>
          <button
            type="button"
            onClick={() => { setRejectModalOpen(false); setRejectReason(''); setRejectComment(''); }}
            className="flex-1 py-2 border border-hairline text-sm text-slate-600 rounded hover:bg-bone transition-colors"
          >
            {t('approver.queue.bulkRejectModal.cancel', { defaultValue: 'Cancel' })}
          </button>
        </div>
      </Modal>
    </div>
  );
}
